/**
 * SEO Utility Functions for Visibeen Directory
 * Comprehensive SEO helpers for meta tags, Open Graph, Twitter Cards, and more
 */

import { getSiteBaseUrl } from '../config/site';

/**
 * Generate page title with proper formatting
 * @param {string} title - Page specific title
 * @param {boolean} includeBrand - Whether to include brand name
 * @returns {string} Formatted page title
 */
export const generatePageTitle = (title, includeBrand = true) => {
  const brandName = 'Visibeen Directory';
  const maxLength = 60; // Optimal title length for SEO

  if (!title) return brandName;

  const fullTitle = includeBrand ? `${title} | ${brandName}` : title;

  // Truncate if too long
  if (fullTitle.length > maxLength) {
    return fullTitle.substring(0, maxLength - 3) + '...';
  }

  return fullTitle;
};

/**
 * Generate meta description with proper length
 * @param {string} description - Page description
 * @returns {string} Optimized meta description
 */
export const generateMetaDescription = (description) => {
  const minLength = 120;
  const maxLength = 160; // Optimal meta description length

  if (!description) {
    return 'Discover trusted local businesses, services and professionals across India. Find reviews, ratings, contact information, and more on Visibeen Directory.';
  }

  // Ensure minimum length
  if (description.length < minLength) {
    console.warn('Meta description is too short. Recommended: 120-160 characters');
  }

  // Truncate if too long
  if (description.length > maxLength) {
    return description.substring(0, maxLength - 3) + '...';
  }

  return description;
};

/**
 * Generate canonical URL
 * @param {string} path - Page path
 * @returns {string} Full canonical URL
 */
export const generateCanonicalUrl = (path = '') => {
  const baseUrl = getSiteBaseUrl();
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  return `${baseUrl}${cleanPath}`;
};

/**
 * Generate keywords from array
 * @param {Array<string>} keywords - Array of keywords
 * @returns {string} Comma-separated keywords
 */
export const generateKeywords = (keywords = []) => {
  const defaultKeywords = [
    'local business',
    'business directory',
    'India businesses',
    'local services',
    'business listings',
    'find businesses'
  ];

  const allKeywords = [...new Set([...keywords, ...defaultKeywords])];
  return allKeywords.join(', ');
};

/**
 * Generate Open Graph meta tags data
 * @param {Object} params - OG parameters
 * @returns {Object} Open Graph data object
 */
export const generateOpenGraphData = ({
  title,
  description,
  image,
  url,
  type = 'website',
  siteName = 'Visibeen Directory'
}) => {
  const baseUrl = getSiteBaseUrl();

  return {
    'og:title': title,
    'og:description': description,
    'og:image': image || `${baseUrl}/og-image.svg`,
    'og:url': url || (typeof window !== 'undefined' ? window.location.href : baseUrl),
    'og:type': type,
    'og:site_name': siteName,
    'og:locale': 'en_IN'
  };
};

/**
 * Generate Twitter Card meta tags data
 * @param {Object} params - Twitter Card parameters
 * @returns {Object} Twitter Card data object
 */
export const generateTwitterCardData = ({
  title,
  description,
  image,
  card = 'summary_large_image',
  site = '@visibeen',
  creator = '@visibeen'
}) => {
  const baseUrl = getSiteBaseUrl();

  return {
    'twitter:card': card,
    'twitter:title': title,
    'twitter:description': description,
    'twitter:image': image || `${baseUrl}/twitter-card.svg`,
    'twitter:site': site,
    'twitter:creator': creator
  };
};

/**
 * Generate breadcrumb keywords for SEO
 * @param {Array} breadcrumbs - Breadcrumb array
 * @returns {string} Breadcrumb keywords
 */
export const generateBreadcrumbKeywords = (breadcrumbs = []) => {
  return breadcrumbs.map(b => b.name).join(' > ');
};

/**
 * Generate location-based keywords
 * @param {string} city - City name
 * @param {string} state - State name
 * @param {string} category - Category name
 * @returns {Array<string>} Location keywords
 */
export const generateLocationKeywords = (city, state, category) => {
  const keywords = [];

  if (category) {
    keywords.push(
      category,
      `${category} in ${city}`,
      `${category} in ${state}`,
      `best ${category} in ${city}`,
      `top ${category} ${city}`,
      `${category} near me`
    );
  }

  if (city && state) {
    keywords.push(
      `${city} ${state}`,
      `businesses in ${city}`,
      `${city} directory`,
      `local services ${city}`
    );
  }

  return keywords;
};

/**
 * Generate FAQ schema markup
 * @param {Array} faqs - Array of FAQ objects {question, answer}
 * @returns {Object} FAQ Schema markup
 */
export const generateFAQSchema = (faqs = []) => {
  if (!faqs || faqs.length === 0) return null;

  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer
      }
    }))
  };
};

/**
 * Generate rating/review aggregate schema
 * @param {number} rating - Average rating
 * @param {number} reviewCount - Number of reviews
 * @returns {Object} AggregateRating schema
 */
export const generateAggregateRatingSchema = (rating, reviewCount) => {
  if (!rating || !reviewCount) return null;

  return {
    '@type': 'AggregateRating',
    ratingValue: rating,
    reviewCount: reviewCount,
    bestRating: 5,
    worstRating: 1
  };
};

/**
 * Sanitize text for SEO (remove special characters, normalize)
 * @param {string} text - Text to sanitize
 * @returns {string} Sanitized text
 */
export const sanitizeForSEO = (text) => {
  if (!text) return '';

  return text
    .replace(/[^\w\s-]/gi, '') // Remove special characters
    .replace(/\s+/g, ' ') // Normalize whitespace
    .trim();
};

/**
 * Generate slug from text
 * @param {string} text - Text to convert to slug
 * @returns {string} URL-friendly slug
 */
export const generateSlug = (text) => {
  if (!text) return '';

  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim();
};

/**
 * Check if URL is absolute
 * @param {string} url - URL to check
 * @returns {boolean} True if absolute URL
 */
export const isAbsoluteUrl = (url) => {
  return /^https?:\/\//i.test(url);
};

/**
 * Make URL absolute
 * @param {string} url - Relative or absolute URL
 * @returns {string} Absolute URL
 */
export const makeAbsoluteUrl = (url) => {
  if (!url) return '';
  if (isAbsoluteUrl(url)) return url;

  const baseUrl = getSiteBaseUrl();
  const cleanUrl = url.startsWith('/') ? url : `/${url}`;
  return `${baseUrl}${cleanUrl}`;
};

/**
 * Generate complete SEO meta tags object
 * @param {Object} params - SEO parameters
 * @returns {Object} Complete meta tags object
 */
export const generateCompleteSEO = ({
  title,
  description,
  keywords = [],
  image,
  url,
  type = 'website',
  noindex = false,
  nofollow = false,
  canonical,
  author = 'Visibeen',
  publishedTime,
  modifiedTime
}) => {
  const pageTitle = generatePageTitle(title);
  const metaDescription = generateMetaDescription(description);
  const canonicalUrl = canonical || generateCanonicalUrl(url);
  const keywordString = generateKeywords(keywords);

  const robots = [];
  if (noindex) robots.push('noindex');
  if (nofollow) robots.push('nofollow');
  const robotsContent = robots.length > 0 ? robots.join(', ') : 'index, follow';

  const ogData = generateOpenGraphData({
    title: pageTitle,
    description: metaDescription,
    image,
    url: canonicalUrl,
    type
  });

  const twitterData = generateTwitterCardData({
    title: pageTitle,
    description: metaDescription,
    image
  });

  // Helpful defaults for richer previews
  const defaultSocialMeta = {
    ...(ogData['og:image'] && {
      'og:image:width': '1200',
      'og:image:height': '630'
    })
  };

  return {
    title: pageTitle,
    meta: {
      description: metaDescription,
      keywords: keywordString,
      author,
      robots: robotsContent,
      'google-site-verification': '', // Add your verification code
      viewport: 'width=device-width, initial-scale=1.0',
      'theme-color': '#0b91d6',
      ...(publishedTime && { 'article:published_time': publishedTime }),
      ...(modifiedTime && { 'article:modified_time': modifiedTime }),
      ...ogData,
      ...defaultSocialMeta,
      ...twitterData
    },
    link: {
      canonical: canonicalUrl
    }
  };
};

/**
 * Generate structured data for local business
 * @param {Object} business - Business data
 * @returns {Object} LocalBusiness schema
 */
export const generateLocalBusinessStructuredData = (business) => {
  const baseUrl = getSiteBaseUrl();

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': business.businessType || 'LocalBusiness',
    '@id': `${baseUrl}/${business.slug}#business`,
    name: business.name,
    image: business.logo ? [makeAbsoluteUrl(business.logo)] : [],
    description: business.description || '',
    url: `${baseUrl}/${business.slug}`,
    telephone: business.phone || '',
    email: business.email || '',
    address: {
      '@type': 'PostalAddress',
      streetAddress: business.streetAddress || '',
      addressLocality: business.city || '',
      addressRegion: business.state || '',
      postalCode: business.postalCode || '',
      addressCountry: business.country || 'IN'
    }
  };

  // Add geo coordinates if available
  if (business.latitude && business.longitude) {
    structuredData.geo = {
      '@type': 'GeoCoordinates',
      latitude: business.latitude,
      longitude: business.longitude
    };
  }

  // Add rating if available
  if (business.rating && business.reviewCount) {
    structuredData.aggregateRating = generateAggregateRatingSchema(
      business.rating,
      business.reviewCount
    );
  }

  // Add opening hours if available
  if (business.openingHours && business.openingHours.length > 0) {
    structuredData.openingHoursSpecification = business.openingHours.map(hours => ({
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: hours.dayOfWeek,
      opens: hours.opens,
      closes: hours.closes
    }));
  }

  // Add social profiles
  if (business.socialProfiles && business.socialProfiles.length > 0) {
    structuredData.sameAs = business.socialProfiles.filter(Boolean);
  }

  // Add price range if available
  if (business.priceRange) {
    structuredData.priceRange = business.priceRange;
  }

  return structuredData;
};

/**
 * Generate optimized image alt text
 * @param {string} businessName - Business name
 * @param {string} imageContext - Context of the image (e.g., "logo", "service", "interior")
 * @param {string} city - City name
 * @param {string} category - Business category
 * @returns {string} Optimized alt text
 */
export const generateImageAlt = (businessName, imageContext = '', city = '', category = '') => {
  const parts = [];

  if (imageContext) {
    parts.push(imageContext);
  }

  if (businessName) {
    parts.push(businessName);
  }

  if (category) {
    parts.push(category);
  }

  if (city) {
    parts.push(`in ${city}`);
  }

  return parts.join(' - ');
};

export default {
  generatePageTitle,
  generateMetaDescription,
  generateCanonicalUrl,
  generateKeywords,
  generateOpenGraphData,
  generateTwitterCardData,
  generateBreadcrumbKeywords,
  generateLocationKeywords,
  generateFAQSchema,
  generateAggregateRatingSchema,
  sanitizeForSEO,
  generateSlug,
  isAbsoluteUrl,
  makeAbsoluteUrl,
  generateCompleteSEO,
  generateLocalBusinessStructuredData,
  generateImageAlt
};
